/**
 * Every keyboard shortcut this app has, written down once.
 *
 * There used to be two of these: the `switch` in `app.tsx` that decides what a
 * key does, and a hand-kept table in `ui/Help.tsx` that says what it does. The
 * help file's own header admitted the arrangement and asked whoever added a key
 * to remember both. Phase 6 added three keys and the table drifted the same
 * week -- `Ctrl+W` was written as two alternatives and rendered as "Ctrl or W",
 * documenting a key that does nothing next to one that was never named.
 *
 * So this module owns the *description* of a shortcut -- which keys, in which
 * group, described how -- and nothing else. `app.tsx` owns what each one does,
 * as a `Record` keyed by {@link ShortcutId}: adding an entry here without a
 * handler there, or a handler without an entry, is a type error rather than a
 * help screen that lies. That is the whole point of splitting it this way, and
 * it is why the actions are deliberately *not* in this file -- the help sheet
 * would otherwise import the entire action graph to draw a list of letters.
 *
 * Matching lives here too, so the keys someone reads are literally the keys the
 * handler compares against. A documented shortcut that does nothing is the one
 * failure this arrangement is built to make impossible.
 */

/**
 * One press.
 *
 * `key` is compared against `KeyboardEvent.key`, which already folds the shift
 * state into the character: `Shift+U` arrives as `"U"` and `#` arrives as `"#"`
 * whatever the layout did to produce it. So unmodified shortcuts carry the
 * literal and nothing else, which is exactly what the handler compared before
 * this table existed.
 *
 * `ctrl` means Control *or* Command -- one entry, because no shortcut here
 * wants to tell a Mac from a PC. `shift` is only meaningful alongside `ctrl`:
 * with a modifier held, platforms disagree about whether the letter arrives
 * shifted, so those are matched case-blind with the shift state checked apart.
 */
export interface Press {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
}

export type ShortcutId =
  | "next"
  | "prev"
  | "open"
  | "back"
  | "search"
  | "escape"
  | "refresh"
  | "select"
  | "archive"
  | "trash"
  | "flag"
  | "read"
  | "unread"
  | "compose"
  | "reply"
  | "replyAll"
  | "forward"
  | "filterUnread"
  | "filterFlagged"
  | "filterAttachment"
  | "filtersClear"
  | "closeTab"
  | "reattach"
  | "help";

export interface Shortcut {
  id: ShortcutId;
  /**
   * The ways to press it: alternatives, or the two steps in order when
   * {@link Shortcut.chord} is set.
   */
  press: Press[];
  /** Pressed one after the other rather than instead of each other. */
  chord?: boolean;
  /**
   * Whether the key is taken from the page when it fires.
   *
   * Set where the browser would otherwise do something with it -- scroll on the
   * arrows, quick-find on `/`, close the window on `Ctrl+W` -- and left off
   * everywhere else, because swallowing a key nobody else wanted is how a
   * shortcut quietly breaks an assistive technology that did want it.
   */
  preventDefault?: boolean;
  what: string;
}

export interface Group {
  title: string;
  note?: string;
  keys: Shortcut[];
}

/**
 * Grouped by what the keys are for, rather than alphabetically or in the order
 * the handler happens to test them. Someone opens the help sheet having just
 * wondered how to do one particular thing, and the group is how they find it.
 */
export const GROUPS: Group[] = [
  {
    title: "Moving around",
    keys: [
      {
        id: "next",
        press: [{ key: "j" }, { key: "ArrowDown" }],
        preventDefault: true,
        what: "Next message",
      },
      {
        id: "prev",
        press: [{ key: "k" }, { key: "ArrowUp" }],
        preventDefault: true,
        what: "Previous message",
      },
      {
        id: "open",
        press: [{ key: "Enter" }, { key: "o" }],
        what: "Open the message under the cursor",
      },
      {
        id: "back",
        press: [{ key: "u" }],
        what: "Back one step: closes the message, or leaves Settings",
      },
      { id: "search", press: [{ key: "/" }], preventDefault: true, what: "Jump to the search box" },
      {
        id: "escape",
        press: [{ key: "Escape" }],
        what: "Clear the ticks if there are any, otherwise go back",
      },
      { id: "refresh", press: [{ key: "r" }], what: "Fetch new mail now" },
    ],
  },
  {
    title: "Doing something about it",
    keys: [
      {
        id: "select",
        press: [{ key: "x" }],
        preventDefault: true,
        what: "Tick the message under the cursor",
      },
      { id: "archive", press: [{ key: "e" }], what: "Archive" },
      { id: "trash", press: [{ key: "#" }], what: "Move to Trash" },
      { id: "flag", press: [{ key: "s" }], what: "Flag, or unflag" },
      { id: "read", press: [{ key: "I" }], what: "Mark as read" },
      { id: "unread", press: [{ key: "U" }], what: "Mark as unread" },
    ],
  },
  {
    title: "Writing",
    note: "These four open the composer, so none of them will interrupt a message already being written.",
    keys: [
      { id: "compose", press: [{ key: "c" }], what: "A new message" },
      { id: "reply", press: [{ key: "R" }], what: "Reply to the sender" },
      { id: "replyAll", press: [{ key: "A" }], what: "Reply to everyone" },
      { id: "forward", press: [{ key: "F" }], what: "Forward" },
    ],
  },
  {
    title: "Filtering the list",
    note: "Two keys, one after the other: press f, then the second key within a second or so.",
    keys: [
      {
        id: "filterUnread",
        press: [{ key: "f" }, { key: "u" }],
        chord: true,
        preventDefault: true,
        what: "Unread only",
      },
      {
        id: "filterFlagged",
        press: [{ key: "f" }, { key: "f" }],
        chord: true,
        preventDefault: true,
        what: "Flagged only",
      },
      {
        id: "filterAttachment",
        press: [{ key: "f" }, { key: "a" }],
        chord: true,
        preventDefault: true,
        what: "Has an attachment",
      },
      {
        id: "filtersClear",
        press: [{ key: "f" }, { key: "c" }],
        chord: true,
        preventDefault: true,
        what: "Clear every filter",
      },
    ],
  },
  {
    /*
     * Desktop only, and listed anyway.
     *
     * The browser build has no tabs, so these do nothing there -- but the sheet
     * is shared, and a shortcut that exists on one build and is absent from the
     * list on both is exactly the drift this file was written to end. Better a
     * line somebody on the web never needs than a key on the desktop that
     * nothing documents.
     */
    title: "Tabs and windows",
    note: "The desktop app only. Escape deliberately closes neither a tab nor a window.",
    keys: [
      {
        id: "closeTab",
        press: [{ key: "w", ctrl: true }],
        preventDefault: true,
        what: "Close the tab you are reading",
      },
      {
        id: "reattach",
        press: [{ key: "m", ctrl: true, shift: true }],
        preventDefault: true,
        what: "Move a detached message back into the main window",
      },
    ],
  },
  {
    title: "This screen",
    keys: [{ id: "help", press: [{ key: "?" }], what: "Show the shortcuts" }],
  },
];

/** Every shortcut, flattened, in the order the groups list them. */
export const ALL: Shortcut[] = GROUPS.flatMap((group) => group.keys);

/**
 * The key that opens a chord.
 *
 * Derived rather than written down again, because "the chord starts with `f`"
 * was a third place the letter appeared. Every chord entry shares a first
 * press; `shortcuts.test.ts` holds them to it.
 */
export const CHORD_LEAD: Press | null = ALL.find((s) => s.chord)?.press[0] ?? null;

/**
 * Does this event press this key?
 *
 * Alt is refused outright at every level: an Alt combination belongs to the OS
 * menu bar or to a compose sequence, and taking one to archive mail is the kind
 * of thing that gets a client uninstalled.
 */
function pressed(press: Press, e: KeyboardEvent): boolean {
  if (e.altKey) return false;
  const mod = e.ctrlKey || e.metaKey;
  if (mod !== (press.ctrl ?? false)) return false;
  if (!press.ctrl) return e.key === press.key;
  // See {@link Press}: shifted letters arrive inconsistently once a modifier is
  // in play, so the letter is compared without case and shift is its own test.
  return e.key.toLowerCase() === press.key.toLowerCase() && e.shiftKey === (press.shift ?? false);
}

/**
 * The shortcut this event fires, if any.
 *
 * `chord` says a chord is pending, and it changes which half of the table is
 * live: a chord's second key means nothing on its own, and every other shortcut
 * is suspended while one is in flight. That is deliberate -- `f` then `e`
 * filters nothing *and archives nothing*, because a chord falling through to an
 * archive is worse than a swallowed keystroke.
 */
export function match(e: KeyboardEvent, chord: boolean): Shortcut | null {
  for (const shortcut of ALL) {
    if (!!shortcut.chord !== chord) continue;
    const step = chord ? shortcut.press.slice(1) : shortcut.press;
    if (step.some((press) => pressed(press, e))) return shortcut;
  }
  return null;
}

/** Whether this event is the first key of a chord. */
export function opensChord(e: KeyboardEvent): boolean {
  return CHORD_LEAD !== null && pressed(CHORD_LEAD, e);
}

/**
 * Keys whose names are not what anyone would call them out loud.
 *
 * Only for display. The handler never sees these -- it compares the `key` field
 * above, which is the browser's spelling, so the two can never disagree about
 * which physical key is meant.
 */
const NAMES: Record<string, string> = {
  Escape: "Esc",
  ArrowDown: "↓",
  ArrowUp: "↑",
};

/**
 * One press, spelled for a human: `Ctrl+Shift+M`, `Shift+U`, `Esc`, `↓`.
 *
 * The modifiers are reconstructed from the same fields the matcher reads, which
 * is the point: a lone capital letter cannot be typed without Shift, so `Shift`
 * is inferred rather than written beside it and forgotten.
 *
 * Unmodified keys are drawn exactly as they are matched -- lowercase `j`, `#`,
 * `/` -- because that is the character on the key someone is being asked to
 * press. Only a `Ctrl` combination is capitalised, which is how every other
 * application writes one.
 */
export function spell(press: Press): string[] {
  const parts: string[] = [];
  if (press.ctrl) parts.push("Ctrl");
  if (press.shift === true || /^[A-Z]$/.test(press.key)) parts.push("Shift");
  parts.push(NAMES[press.key] ?? (press.ctrl ? press.key.toUpperCase() : press.key));
  return parts;
}
